import type { CookiesConsentValue } from '~/constants/cookies'
import { EventName, PropName } from '~/constants/plausible'

type CookiesConsentLocalStorageItem = {
  value: string
  timestamp: number
}

type CookiesConsent = {
  value: CookiesConsentValue
  isExpired: boolean
}

const LOCAL_STORAGE_ITEM_KEY = 'cookiesConsent'
const COOKIE_CONSENT_VALIDITY_DAYS = 390

const { trackEvent } = usePlausible()
const isLocalStorageSupported = testLocalStorageSupport()
const cookiesConsent = getCookiesConsent()
const showCookieModal = !cookiesConsent || cookiesConsent.isExpired

export function useCookiesConsent() {
  return {
    showCookieModal,
    getCookiesConsent,
    setCookiesConsent
  }
}

function getCookiesConsent(): CookiesConsent {
  if (!isLocalStorageSupported) {
    return
  }

  const localStorageItem = localStorage.getItem(LOCAL_STORAGE_ITEM_KEY)
  if (!localStorageItem) {
    return
  }

  const cookiesConsentLocalStorageItem = JSON.parse(localStorageItem) as CookiesConsentLocalStorageItem
  return {
    value: cookiesConsentLocalStorageItem.value as CookiesConsentValue,
    isExpired: Date.now() > cookiesConsentLocalStorageItem.timestamp + COOKIE_CONSENT_VALIDITY_DAYS * 86_400_000
  }
}

function setCookiesConsent(cookiesConsentValue: CookiesConsentValue) {
  if (!isLocalStorageSupported) {
    return
  }

  const cookiesConsentLocalStorageItem: CookiesConsentLocalStorageItem = {
    value: cookiesConsentValue,
    timestamp: Date.now()
  }

  localStorage.setItem(LOCAL_STORAGE_ITEM_KEY, JSON.stringify(cookiesConsentLocalStorageItem))

  trackEvent(EventName.SET_COOKIE_CONSENT, {
    props: { [PropName.CHOICE]: cookiesConsentValue }
  })
}
